import logger from '@/utils/logger'
import { ShwEnv } from '@/constants/types'

import type { UserContextType } from '@/client/context/user'
import { WEBSITE_ENV } from '@/client/settings'

import googleAnalytics from '@/vendor/googleAnalytics'
import posthog from '@/vendor/posthog'

import { isUserDefined } from '../user'
import getUserTag from './getUserTag'

export interface TrackEventProps {
  category: string // This is the event name, e.g. 'Button Click'
  action: string // e.g. 'Session Join'
}
export type TrackEventExtraProps = Record<string, unknown>

const isTest = WEBSITE_ENV !== ShwEnv.PRODUCTION
let lastRoute: string | null = null

export function trackPageView(route: string): void {
  lastRoute = route

  if (isTest) {
    return
  }

  try {
    googleAnalytics.send({ hitType: 'pageview', page: route })
    // Posthog doesn't capture the generic route name by default, but it's useful
    // for analytical purposes (e.g. we want the generic route: `/series/[id]`).
    posthog.capture('$pageview', { route })
  } catch (err) {
    logger.error('Error in trackPageView', {
      err,
    })
  }
}

export function trackEvent(
  { category, action }: TrackEventProps,
  extra?: TrackEventExtraProps
): void {
  if (isTest) {
    return
  }

  try {
    googleAnalytics.event({
      category,
      action,
    })
    posthog.capture(category, {
      ...extra,
      action,
      route: lastRoute,
    })
    logger.info(`Tracked event: ${category}`, { ...extra, action, route: lastRoute })
  } catch (err) {
    logger.error('Error in trackEvent', { err })
  }
}

let lastIdentifiedUserId: number | null = null
export function identifyUser(user: UserContextType): void {
  if (isTest) {
    return
  }

  const { id: userId } = user
  const userTag = getUserTag(user)

  if (userId === lastIdentifiedUserId) {
    // Ignore post-navigation unnecessary identification
    return
  }
  lastIdentifiedUserId = userId

  try {
    if (isUserDefined(user)) {
      posthog.identify(userId.toString())
      posthog.people.set_once({
        name: userId.toString(),
        role: userTag,
      })
      googleAnalytics.set({ dimension1: userId })
    } else {
      posthog.reset()
      googleAnalytics.set({ dimension1: null })
    }
  } catch (err) {
    logger.error('Error in identifyUser', { err })
  }
}
